import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import { PriorityBadge, TicketStatusBadge } from "../components/ui/index.jsx";
import {
  adminGetTicket,
  adminUpdateTicket,
  adminDeleteTicket,
  adminGetComments,
  adminAddComment,
} from "../services/ticketService.js";

// ─────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────

const STATUSES = [
  { value: "OPEN",        label: "Ouvert",     color: "#3B82F6" },
  { value: "IN_PROGRESS", label: "En cours",   color: "#F59E0B" },
  { value: "PENDING",     label: "En attente", color: "#8B5CF6" },
  { value: "RESOLVED",    label: "Résolu",     color: "#10B981" },
  { value: "CLOSED",      label: "Fermé",      color: "#6B7280" },
];

const PRIORITIES = [
  { value: "LOW",      label: "Basse",    color: "#6B7280" },
  { value: "MEDIUM",   label: "Moyenne",  color: "#F59E0B" },
  { value: "HIGH",     label: "Haute",    color: "#EF4444" },
  { value: "CRITICAL", label: "Critique", color: "#7C3AED" },
];

/**
 * Nettoie le HTML d'Atera pour un affichage lisible.
 */
function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function getInitials(author) {
  if (!author) return "?";
  return `${author.firstName?.[0] ?? ""}${author.lastName?.[0] ?? ""}`.toUpperCase() || "?";
}

function getFullName(author) {
  if (!author) return "Inconnu";
  return `${author.firstName ?? ""} ${author.lastName ?? ""}`.trim() || "Inconnu";
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function TicketDetailPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();

  // ── Données ──────────────────────────────────
  const [ticket,   setTicket]   = useState(null);
  const [comments, setComments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // ── Onglet actif ─────────────────────────────
  const [tab, setTab] = useState("conversation");

  // ── Commentaire ──────────────────────────────
  const [newComment,  setNewComment]  = useState("");
  const [sendingCmnt, setSendingCmnt] = useState(false);

  // ── Modification en ligne ────────────────────
  const [saving, setSaving] = useState(false);

  // Chargement initial
  useEffect(() => { fetchAll(); }, [id]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ticketRes, commentsRes] = await Promise.all([
        adminGetTicket(id),
        adminGetComments(id),
      ]);
      setTicket(ticketRes.data ?? ticketRes);
      setComments(Array.isArray(commentsRes) ? commentsRes : commentsRes.data ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Modification du statut ou de la priorité
  const handleUpdate = async (field, value) => {
    setSaving(true);
    try {
      const res = await adminUpdateTicket(id, { [field]: value });
      setTicket(res.data ?? res);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Envoi d'un commentaire
  const handleSendComment = async () => {
    if (!newComment.trim()) return;
    setSendingCmnt(true);
    try {
      const res = await adminAddComment(id, { content: newComment });
      setComments(prev => [...prev, res.data ?? res]);
      setNewComment("");
      // Met à jour updatedAt affiché
      setTicket(prev => ({ ...prev, updatedAt: new Date().toISOString() }));
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSendingCmnt(false);
    }
  };

  // Suppression du ticket
  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce ticket définitivement ?")) return;
    try {
      await adminDeleteTicket(id);
      navigate("/tickets");
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // ── États intermédiaires ─────────────────────
  if (loading) return (
    <>
      <Topbar title="Tickets" subtitle="détail"/>
      <div className="page-body" style={{ textAlign:"center", padding:60, color:"var(--text-3)" }}>
        <i className="bi bi-arrow-repeat" style={{ fontSize:24, display:"block", marginBottom:8 }}/>
        Chargement du ticket…
      </div>
    </>
  );

  if (error || !ticket) return (
    <>
      <Topbar title="Tickets" subtitle="détail"/>
      <div className="page-body">
        <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:10, padding:14, color:"var(--danger)", display:"flex", alignItems:"center", gap:12 }}>
          <i className="bi bi-exclamation-triangle"/>{error || "Ticket introuvable."}
          <button className="btn-itm btn-itm-outline" style={{ marginLeft:"auto" }} onClick={fetchAll}>Réessayer</button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Topbar title="Tickets" subtitle="détail"/>
      <div className="page-body">

        {/* Breadcrumb */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, fontSize:13, color:"var(--text-3)" }}>
          <span style={{ cursor:"pointer", color:"var(--accent)" }} onClick={() => navigate("/tickets")}>
            <i className="bi bi-arrow-left me-1"/>Tickets
          </span>
          <i className="bi bi-chevron-right" style={{ fontSize:10 }}/>
          <span style={{ color:"var(--text-2)" }}>
            {ticket.ateraTicketId ? `#${ticket.ateraTicketId}` : ticket.title}
          </span>
          {ticket.company && (
            <>
              <i className="bi bi-chevron-right" style={{ fontSize:10 }}/>
              <span style={{ color:"var(--accent)" }}>{ticket.company.name}</span>
            </>
          )}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:20, alignItems:"start" }}>

          {/* ── Colonne principale ── */}
          <div>
            {/* En-tête du ticket */}
            <div className="itm-card anim-fade-up" style={{ marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:14 }}>
                <h2 style={{ fontFamily:"Syne,sans-serif", fontSize:18, fontWeight:800, color:"var(--text-1)", margin:0 }}>
                  {ticket.title}
                </h2>
                {/* Bouton supprimer */}
                <button className="btn-itm btn-itm-outline"
                  onClick={handleDelete}
                  style={{ color:"var(--danger)", borderColor:"rgba(239,68,68,.3)", flexShrink:0 }}>
                  <i className="bi bi-trash me-1"/>Supprimer
                </button>
              </div>

              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom: ticket.description ? 14 : 0 }}>
                <PriorityBadge priority={ticket.priority}/>
                <TicketStatusBadge status={ticket.status}/>
                {ticket.category && (
                  <span style={{ background:"var(--navy-soft)", borderRadius:20, padding:"3px 9px", fontSize:11, color:"var(--text-2)" }}>
                    <i className="bi bi-folder me-1"/>{ticket.category}
                  </span>
                )}
                {ticket.company && (
                  <span style={{ background:"var(--accent-dim)", borderRadius:20, padding:"3px 9px", fontSize:11, color:"var(--accent)" }}>
                    <i className="bi bi-building me-1"/>{ticket.company.name}
                  </span>
                )}
                {saving && <i className="bi bi-arrow-repeat" style={{ fontSize:14, color:"var(--text-3)", animation:"spin .7s linear infinite" }}/>}
              </div>

              {ticket.description && (
                <p style={{ fontSize:13, color:"var(--text-2)", lineHeight:1.7, padding:12, background:"var(--navy-soft)", borderRadius:10, whiteSpace:"pre-wrap", margin:0 }}>
                  {stripHtml(ticket.description)}
                </p>
              )}
            </div>

            {/* Onglets */}
            <div className="itm-tabs">
              {[
                { id:"conversation", label:"Conversation", count:comments.length },
              ].map(t => (
                <div key={t.id} className={`itm-tab ${tab===t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                  {t.label}
                  <span style={{ marginLeft:6, background:"var(--navy-soft)", borderRadius:20, padding:"1px 7px", fontSize:11, color:"var(--text-2)" }}>
                    {t.count}
                  </span>
                </div>
              ))}
            </div>

            {/* Conversation */}
            {tab === "conversation" && (
              <div className="anim-fade-in">
                <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:16 }}>
                  {comments.length === 0 && (
                    <div style={{ textAlign:"center", padding:30, color:"var(--text-3)", fontSize:13 }}>
                      Aucun commentaire pour le moment.
                    </div>
                  )}
                  {comments.map(c => (
                    <div key={c.id} style={{ display:"flex", gap:12 }}>
                      <div style={{ width:34, height:34, borderRadius:"50%", flexShrink:0, background:"linear-gradient(135deg,var(--accent3),var(--accent))", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff" }}>
                        {getInitials(c.author)}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                          <span style={{ fontWeight:600, fontSize:13, color:"var(--text-1)" }}>
                            {getFullName(c.author)}
                          </span>
                          <span style={{ fontSize:11, color:"var(--text-3)", marginLeft:"auto" }}>
                            {c.createdAt ? new Date(c.createdAt).toLocaleString("fr-FR") : ""}
                          </span>
                        </div>
                        <div style={{ background:"var(--navy-soft)", border:"1px solid var(--border)", borderRadius:12, padding:"10px 14px", fontSize:13, color:"var(--text-2)", lineHeight:1.7 }}>
                          {c.content}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Zone de réponse */}
                <div className="itm-card" style={{ padding:14 }}>
                  <textarea className="itm-input" rows={3}
                    placeholder="Votre réponse…"
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    style={{ resize:"vertical", marginBottom:10 }}/>
                  <div style={{ display:"flex", justifyContent:"flex-end" }}>
                    <button className="btn-itm btn-itm-primary"
                      onClick={handleSendComment}
                      disabled={!newComment.trim() || sendingCmnt}>
                      <i className="bi bi-send"/> {sendingCmnt ? "Envoi…" : "Envoyer"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Colonne latérale ── */}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

            {/* Modifier statut */}
            <div className="itm-card anim-fade-up">
              <div className="card-title" style={{ marginBottom:12 }}>Statut</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {STATUSES.map(s => (
                  <button key={s.value}
                    onClick={() => handleUpdate("status", s.value)}
                    style={{
                      padding:"8px 12px", borderRadius:8, border:"1px solid",
                      borderColor: ticket.status===s.value ? s.color : "var(--border)",
                      background:  ticket.status===s.value ? s.color + "20" : "transparent",
                      color:       ticket.status===s.value ? s.color : "var(--text-2)",
                      fontSize:12, cursor:"pointer", textAlign:"left", fontWeight: ticket.status===s.value ? 600 : 400,
                      transition:"all .15s",
                    }}>
                    {ticket.status===s.value && <i className="bi bi-check2 me-2"/>}
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Modifier priorité */}
            <div className="itm-card anim-fade-up delay-1">
              <div className="card-title" style={{ marginBottom:12 }}>Priorité</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {PRIORITIES.map(p => (
                  <button key={p.value}
                    onClick={() => handleUpdate("priority", p.value)}
                    style={{
                      padding:"8px 12px", borderRadius:8, border:"1px solid",
                      borderColor: ticket.priority===p.value ? p.color : "var(--border)",
                      background:  ticket.priority===p.value ? p.color + "20" : "transparent",
                      color:       ticket.priority===p.value ? p.color : "var(--text-2)",
                      fontSize:12, cursor:"pointer", textAlign:"left", fontWeight: ticket.priority===p.value ? 600 : 400,
                      transition:"all .15s",
                    }}>
                    {ticket.priority===p.value && <i className="bi bi-check2 me-2"/>}
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Informations */}
            <div className="itm-card anim-fade-up delay-2">
              <div className="card-title" style={{ marginBottom:12 }}>Informations</div>
              {[
                ["Entreprise",   ticket.company?.name ?? "—"],
                ["Catégorie",    ticket.category ?? "—"],
                ["Créé par",     getFullName(ticket.createdBy)],
                ["Créé le",      ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString("fr-FR") : "—"],
                ["Mis à jour",   ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleDateString("fr-FR") : "—"],
              ].map(([k, v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                  <span style={{ color:"var(--text-3)" }}>{k}</span>
                  <span style={{ color:"var(--text-2)", textAlign:"right", maxWidth:"60%" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}